import React, { useState, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  Alert,
  LinearProgress,
  Divider,
  useMediaQuery,
  useTheme,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Delete,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { getDuplicateDescription } from '../utils/duplicateDetection';

const DuplicateReviewDialog = memo(({ 
  open, 
  onClose, 
  duplicateGroups, 
  onResolveDuplicates 
}) => {
  const [resolutions, setResolutions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [processing, setProcessing] = useState(false);
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize resolutions for each group when dialog opens
  React.useEffect(() => {
    if (open && duplicateGroups.length > 0) {
      const initialResolutions = {};
      duplicateGroups.forEach(group => {
        // Default to keeping the first requirement (usually the one created first)
        initialResolutions[group.id] = {
          action: 'keep-first',
          keepIds: [group.requirements[0].id],
          deleteIds: group.requirements.slice(1).map(req => req.id)
        };
      });
      setResolutions(initialResolutions);
      
      // Expand first group by default
      setExpandedGroups({ [duplicateGroups[0].id]: true });
    }
  }, [open, duplicateGroups]);

  const handleResolutionChange = (groupId, action, requirement = null) => {
    setResolutions(prev => {
      const group = duplicateGroups.find(g => g.id === groupId);
      if (!group) return prev;

      let newResolution = { action };

      switch (action) {
        case 'keep-both':
          newResolution.keepIds = group.requirements.map(req => req.id);
          newResolution.deleteIds = [];
          break;
        case 'keep-first':
          newResolution.keepIds = [group.requirements[0].id];
          newResolution.deleteIds = group.requirements.slice(1).map(req => req.id);
          break;
        case 'keep-second':
          newResolution.keepIds = [group.requirements[1].id];
          newResolution.deleteIds = [group.requirements[0].id, ...group.requirements.slice(2).map(req => req.id)];
          break;
        case 'keep-specific':
          if (requirement) {
            newResolution.keepIds = [requirement.id];
            newResolution.deleteIds = group.requirements.filter(req => req.id !== requirement.id).map(req => req.id);
          }
          break;
        case 'delete-all':
          newResolution.keepIds = [];
          newResolution.deleteIds = group.requirements.map(req => req.id);
          break;
        default:
          return prev;
      }

      return {
        ...prev,
        [groupId]: newResolution
      };
    });
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleApplyResolutions = async () => {
    setProcessing(true);
    try {
      // Collect all deletions to perform
      const deleteIds = [];
      Object.values(resolutions).forEach(resolution => {
        deleteIds.push(...resolution.deleteIds);
      });

      // Call the callback with the resolution data
      await onResolveDuplicates(deleteIds);
      onClose();
    } catch (error) {
      console.error('Error resolving duplicates:', error);
    } finally {
      setProcessing(false);
    }
  };

  const getResolutionSummary = () => {
    const totalGroups = duplicateGroups.length;
    const totalRequirements = duplicateGroups.reduce((sum, group) => sum + group.requirements.length, 0);
    const totalDeletions = Object.values(resolutions).reduce((sum, resolution) => sum + resolution.deleteIds.length, 0);
    
    return {
      totalGroups,
      totalRequirements,
      totalDeletions,
      willKeep: totalRequirements - totalDeletions
    };
  };

  const summary = getResolutionSummary();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      aria-labelledby="duplicate-review-title"
      PaperProps={{
        sx: isPhone ? undefined : { minHeight: '60vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle id="duplicate-review-title" component="div">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" fontSize="small" />
          <Typography variant="h6" component="h2">
            Review duplicate requirements
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Found {duplicateGroups.length} group{duplicateGroups.length !== 1 ? 's' : ''} with potential duplicates.
          Choose how to resolve each group.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {processing && <LinearProgress sx={{ mb: 2 }} />}
        
        {/* Summary */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {summary.totalRequirements} requirements in {summary.totalGroups} group{summary.totalGroups !== 1 ? 's' : ''}.
          Keeping {summary.willKeep}, deleting {summary.totalDeletions}.
        </Alert>

        {/* Duplicate Groups */}
        {duplicateGroups.map((group, groupIndex) => (
          <Card key={group.id} variant="outlined" sx={{ mb: 1.5, borderRadius: 'var(--q-radius-xl)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                  Group {groupIndex + 1} · {group.requirements.length} requirements
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={`${Math.round(group.maxSimilarity * 100)}% similar`}
                    size="small"
                    color={group.maxSimilarity >= 0.9 ? 'error' : group.maxSimilarity >= 0.8 ? 'warning' : 'default'}
                  />
                  <IconButton
                    size="small"
                    onClick={() => toggleGroupExpanded(group.id)}
                    aria-expanded={Boolean(expandedGroups[group.id])}
                    aria-label={expandedGroups[group.id] ? 'Hide requirements' : 'Show requirements'}
                  >
                    {expandedGroups[group.id] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              {/* Resolution Options */}
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ fontSize: '0.8125rem' }}>Resolution</FormLabel>
                <RadioGroup
                  value={resolutions[group.id]?.action || 'keep-first'}
                  onChange={(e) => handleResolutionChange(group.id, e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="keep-both"
                    control={<Radio size="small" />}
                    label="Keep both"
                  />
                  <FormControlLabel
                    value="keep-first"
                    control={<Radio size="small" />}
                    label="Keep first"
                  />
                  {group.requirements.length === 2 && (
                    <FormControlLabel
                      value="keep-second"
                      control={<Radio size="small" />}
                      label="Keep second"
                    />
                  )}
                  <FormControlLabel
                    value="delete-all"
                    control={<Radio size="small" />}
                    label="Delete all"
                  />
                </RadioGroup>
              </FormControl>

              {/* Expanded Details */}
              <Collapse in={expandedGroups[group.id]}>
                <Box>
                  {/* Duplicate Matches Description */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {group.matches.map((match, index) => (
                      <Box key={index} sx={{ mb: 1 }}>
                        • {getDuplicateDescription(match)}
                      </Box>
                    ))}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Requirements Preview */}
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Requirements</Typography>
                  {group.requirements.map((requirement, reqIndex) => {
                    const isKept = resolutions[group.id]?.keepIds?.includes(requirement.id);
                    const isDeleted = resolutions[group.id]?.deleteIds?.includes(requirement.id);
                    
                    return (
                      <Card 
                        key={requirement.id} 
                        variant="outlined" 
                        sx={{ 
                          mb: 1,
                          borderRadius: 'var(--q-radius-lg)',
                          opacity: isDeleted ? 0.6 : 1,
                          borderColor: isKept ? 'success.main' : isDeleted ? 'error.main' : 'divider',
                          bgcolor: isKept ? 'var(--q-success-soft)' : 'background.paper'
                        }}
                      >
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2">
                                  {requirement.title}
                                </Typography>
                                <Chip label={requirement.type} size="small" />
                                <Chip 
                                  label={requirement.priority} 
                                  size="small" 
                                  color={
                                    requirement.priority === 'High' ? 'error' : 
                                    requirement.priority === 'Medium' ? 'warning' : 'default'
                                  }
                                />
                                {isKept && <CheckCircle color="success" fontSize="small" />}
                                {isDeleted && <Delete color="error" fontSize="small" />}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {requirement.description}
                              </Typography>
                            </Box>
                            {group.requirements.length > 2 && (
                              <Button
                                size="small"
                                variant={isKept ? "contained" : "outlined"}
                                color={isKept ? "success" : "primary"}
                                onClick={() => handleResolutionChange(group.id, 'keep-specific', requirement)}
                                sx={{ ml: 1 }}
                              >
                                {isKept ? 'Keeping' : 'Keep This'}
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button 
          onClick={handleApplyResolutions}
          variant="contained"
          color="primary"
          disabled={processing}
          startIcon={processing ? undefined : <CheckCircle />}
        >
          {processing
            ? 'Applying...'
            : summary.totalDeletions
              ? `Delete ${summary.totalDeletions} duplicate${summary.totalDeletions !== 1 ? 's' : ''}`
              : 'Keep all and close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DuplicateReviewDialog.displayName = 'DuplicateReviewDialog';

export default DuplicateReviewDialog; 